import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { environment } from '../../../../environments/environment';
import { MatChipsModule } from '@angular/material/chips';

export interface AccordionLink {
  id?: string | number | null | any;
  label: string;
  url: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
  content?: string;
}

export interface MenuAction {
  action: string;
  label: string;
  icon: string;
  disabled?: boolean;
}

export interface AccordionItem {
  id?: string;
  title: string;
  type: 'links' | 'accordion' | 'custom';
  description?: string;
  icon?: string;
  expanded?: boolean;
  disabled?: boolean;
  loading?: boolean;
  badge?: string | number;
  actions?: boolean;
  actionIcon?: string;
  actionTooltip?: string;
  links?: AccordionLink[];
  children?: AccordionItem[];
  customTemplate?: TemplateRef<any>;
  data?: any;
  menuActions?: MenuAction[];
  selected?: boolean;
}

@Component({
  selector: 'app-expansion-panel',
  standalone: true,
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.scss'],
  imports: [
    CommonModule,
    MatExpansionModule, // <mat-accordion>, <mat-expansion-panel>
    MatTooltipModule, // matTooltip
    MatButtonModule, // mat-button
    MatMenuModule, // mat-menu
    MatDividerModule, // mat-divider
    MatChipsModule,
  ],
  host: {
    '[class]': "'nested-level-' + nestedLevel",
  },
})
export class ExpansionPanelComponent implements OnChanges {
  @Input() items: AccordionItem[] | any = [];
  @Input() nestedLevel: number = 0;
  @Input() selectedId: any;

  @Output() itemExpanded = new EventEmitter<AccordionItem>();
  @Output() itemCollapsed = new EventEmitter<AccordionItem>();
  @Output() linkClicked = new EventEmitter<AccordionLink>();
  @Output() actionClicked = new EventEmitter<{
    action: string;
    item: AccordionItem;
  }>();

  accordionItems: AccordionItem[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      if (this.items && !Array.isArray(this.items) && this.items.rows) {
        this.accordionItems = this.transformApiResponse(this.items.rows);
      } else if (Array.isArray(this.items)) {
        this.accordionItems = [...this.items];
      }
    }
  }

  private transformApiResponse(rows: any[]): AccordionItem[] {
    return rows.map((apiItem) => {
      const item: AccordionItem = {
        id: apiItem.id?.toString(),
        title: apiItem.title || apiItem.name || 'Untitled',
        description: apiItem.description || '',
        type: apiItem.type || 'links',
        expanded: apiItem.expanded ?? false,
        disabled: apiItem.disabled ?? false,
        actions: true,
        icon: apiItem.icon || 'fas fa-folder',
        badge: apiItem.badge || '',
        data: apiItem,
      };
      if (
        apiItem.children &&
        Array.isArray(apiItem.children) &&
        apiItem.children.length > 0
      ) {
        item.type = 'accordion';
        item.children = this.transformApiResponse(apiItem.children);
      } else if (
        apiItem.accordion_content &&
        Array.isArray(apiItem.accordion_content)
      ) {
        item.type = 'links';
        item.links = apiItem.accordion_content.map((file: any) => ({
          id: file.accordion_details_id,
          label: file.label || 'Untitled',
          url: file.content || '#',
          icon: file.type === 'FILES' ? 'fas fa-file' : 'fas fa-link',
          description: file.description || '',
          disabled: false,
        }));
      } else if (apiItem.links && Array.isArray(apiItem.links)) {
        item.type = 'links';
        item.links = apiItem.links.map((link: any) => ({
          id: link.accordion_details_id,
          label: link.label || 'Unnamed Link',
          url: link.url || '#',
          description: link.description || '',
          icon: link.icon || 'fas fa-link',
          disabled: link.disabled || false,
        }));
      }

      return item;
    });
  }
  private buildBlobUrl(path: string): string {
    const base = environment.ASSET_BLOB_URL;
    return base + path;
  }
  onPanelOpened(openedItem: AccordionItem): void {
    // Collapse all others at this level
    this.accordionItems.forEach((item) => {
      item.expanded = item === openedItem;
      item.selected = item === openedItem;
    });
    this.itemExpanded.emit(openedItem);
  }

  onPanelClosed(closedItem: AccordionItem): void {
    closedItem.expanded = false;
    closedItem.selected = false;
    if (closedItem.children && closedItem.children.length > 0) {
      this.collapseAllChildren(closedItem.children);
    }

    this.itemCollapsed.emit(closedItem);
  }

  private collapseAllChildren(children: AccordionItem[]): void {
    children.forEach((child) => {
      child.expanded = false;
      child.selected = false;
      if (child.children && child.children.length > 0) {
        this.collapseAllChildren(child.children);
      }
    });
  }

  onMenuAction(action: string, item: AccordionItem, event: Event) {
    this.actionClicked.emit({ action, item });
  }

  onLinkClick(event: Event, link: AccordionLink): void {
    if (link.disabled) {
      event.preventDefault();
      return;
    }

    this.linkClicked.emit(link);

    if (link.url.startsWith('http')) {
      // Open in new tab
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      // Handle internal or special links here
      event.preventDefault();
    }

    event.stopPropagation();
  }

  onLinkMenuAction(action: string, link: AccordionLink) {
    this.actionClicked.emit({
      action,
      item: { id: link.id, title: link.label, type: 'links', data: link },
    });
  }

  trackByFn(index: number, item: AccordionItem): string {
    return item.id || `${index}-${item.title}`;
  }

  trackLinkByFn(index: number, link: AccordionLink): string {
    return `${index}-${link.url}`;
  }
}
